/**
 * Styles of the trade and fitter blocks, from the site mockup (`site.css`,
 * "Trade": `.account-cards`, `.postcode-box`, `.pc-chip`, `.job`,
 * `.coverage-map`, plus the form primitives they sit in), translated to the
 * theme's `--sk-*` variables and scoped per block.
 *
 * <p>Differences from the mockup, on purpose:
 * <ul>
 *   <li>Brass never carries text (palette rule): the "new" and "example" tags
 *       use `--sk-brass-text` on `--sk-brass-soft`, not brass on brass-soft.</li>
 *   <li>Form controls use `--sk-control-border` for the 3:1 non-text rule.</li>
 *   <li>Mobile first: every grid is one column below 640 px and nothing is wider
 *       than the viewport at 360 px (long codes and titles wrap).</li>
 *   <li>The coverage "map" is a grid of areas, not a drawn map: see the block.</li>
 * </ul>
 */

/** Shared by all three: the section frame, notices, fields and status lines. */
export const TRADE_SHARED_CSS = `
.sk-trade{padding-block:56px}
@media (min-width:769px){.sk-trade{padding-block:clamp(72px,6vw,104px)}}
.sk-trade__notice{margin:0 0 1.25rem;padding:.75rem 1rem;border-radius:var(--sk-radius-sm);background:var(--sk-brass-soft);color:var(--sk-ink);font-size:.88rem}
.sk-trade__panel{background:var(--sk-white);border:1px solid var(--sk-border);border-radius:18px;padding:clamp(1.125rem,.75rem + 1.5vw,1.75rem);min-width:0}
.sk-trade__field{display:grid;gap:6px;min-width:0}
.sk-trade__field + .sk-trade__field{margin-top:1.125rem}
.sk-trade__label{font-size:.8rem;font-weight:700;letter-spacing:.02em;color:var(--sk-muted)}
.sk-trade__hint{font-size:.8rem;color:var(--sk-faint);margin:0}
.sk-trade__error{font-size:.85rem;color:var(--sk-clay);font-weight:700;margin:0}
.sk-trade__input{width:100%;min-width:0;padding:12px 14px;border-radius:var(--sk-radius-sm);border:1px solid var(--sk-control-border);background:#fff;font:inherit;font-size:.95rem;color:var(--sk-ink)}
.sk-trade__input:focus{outline:none;border-color:var(--sk-sage);box-shadow:0 0 0 3px rgba(46,74,62,.15)}
.sk-trade__input[aria-invalid="true"]{border-color:var(--sk-clay)}
.sk-trade__status{margin:.75rem 0 0;font-size:.9rem;font-weight:700}
.sk-trade__status--ok{color:var(--sk-sage)}
.sk-trade__status--error{color:var(--sk-clay)}
.sk-trade__actions{display:flex;flex-wrap:wrap;gap:.75rem;align-items:center;margin-top:1.25rem}
.sk-trade__retry,.sk-trade__mini{min-height:36px;border:1px solid var(--sk-control-border);background:#fff;border-radius:999px;padding:7px 14px;font:inherit;font-weight:700;font-size:.85rem;cursor:pointer;color:var(--sk-ink)}
.sk-trade__retry:hover,.sk-trade__mini:hover{border-color:var(--sk-ink)}
.sk-trade__mini:disabled{opacity:.5;cursor:not-allowed}
.sk-fcov__form{margin:0;min-width:0}
.sk-fcov__form:disabled{opacity:.7}
`;

export const ACCOUNT_TYPES_CSS = `
.sk-acct__grid{display:grid;gap:20px;padding-top:12px}
@media (min-width:900px){.sk-acct__grid{grid-template-columns:repeat(var(--sk-acct-cols,3),minmax(0,1fr))}}
.sk-acct__card{position:relative;display:flex;flex-direction:column;gap:16px;background:var(--sk-white);border:1px solid var(--sk-border);border-radius:18px;padding:clamp(1.375rem,1rem + 1.5vw,1.875rem);min-width:0}
.sk-acct__card--featured{border:2px solid var(--sk-sage);box-shadow:var(--sk-shadow-lg)}
.sk-acct__flag{position:absolute;top:-12px;left:clamp(1.375rem,1rem + 1.5vw,1.875rem);background:var(--sk-sage);color:#fff;font-size:.72rem;font-weight:800;letter-spacing:.06em;padding:5px 11px;border-radius:999px;text-transform:uppercase}
.sk-acct__ico{width:52px;height:52px;border-radius:14px;background:var(--sk-brass-soft);color:var(--sk-brass-text);display:grid;place-items:center}
.sk-acct__card h3{font-family:var(--sk-serif);font-weight:500;font-size:1.4rem;line-height:1.2;margin:0;color:var(--sk-ink)}
.sk-acct__desc{margin:0;color:var(--sk-muted);font-size:.95rem}
.sk-acct__list{list-style:none;margin:0;padding:0;display:grid;gap:10px;font-size:.93rem;flex:1}
.sk-acct__list li{display:flex;gap:10px}
.sk-acct__list li::before{content:'✓';color:var(--sk-sage);font-weight:800;flex:none}
.sk-acct__spacer{flex:1}
.sk-acct__card .sk-btn{width:100%;white-space:normal;text-align:center}
`;

export const FITTER_COVERAGE_CSS = `
.sk-fcov__grid{display:grid;gap:2rem;align-items:start}
@media (min-width:900px){.sk-fcov__grid{grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:2.5rem}}
.sk-fcov__box{display:flex;flex-wrap:wrap;gap:8px;padding:10px;border:1px solid var(--sk-control-border);border-radius:12px;background:#fff;min-height:56px;align-items:center;min-width:0}
.sk-fcov__box:focus-within{border-color:var(--sk-sage);box-shadow:0 0 0 3px rgba(46,74,62,.15)}
.sk-fcov__box[data-invalid]{border-color:var(--sk-clay)}
.sk-fcov__chips{display:contents;list-style:none;margin:0;padding:0}
.sk-fcov__chip{display:inline-flex;align-items:center;gap:4px;background:var(--sk-sage-grey);color:var(--sk-sage);font-weight:800;font-size:.85rem;padding:4px 4px 4px 12px;border-radius:999px}
.sk-fcov__chip button{display:grid;place-items:center;width:28px;height:28px;border:0;border-radius:50%;background:transparent;color:inherit;cursor:pointer;font:inherit;font-size:1.05rem;line-height:1;padding:0}
.sk-fcov__chip button:hover{background:rgba(46,74,62,.12)}
.sk-fcov__input{flex:1 1 140px;min-width:0;border:0;outline:none;padding:6px;font:inherit;font-size:.95rem;text-transform:uppercase;background:transparent;color:var(--sk-ink)}
.sk-fcov__input::placeholder{text-transform:none;color:var(--sk-faint)}
.sk .sk-fcov__input:focus-visible{outline:none}
.sk-fcov__trades{display:flex;flex-wrap:wrap;gap:8px;border:0;margin:0;padding:0;min-width:0}
.sk-fcov__trades legend{padding:0;margin-bottom:6px}
.sk-fcov__trade{display:inline-flex;align-items:center;gap:8px;border:1px solid var(--sk-control-border);border-radius:999px;padding:6px 12px;background:#fff;font-size:.9rem;cursor:pointer}
.sk-fcov__trade input{accent-color:var(--sk-sage);width:16px;height:16px;margin:0}
.sk-fcov__trade:has(input:checked){border-color:var(--sk-sage);background:var(--sk-sage-grey)}
.sk-fcov__map{background:var(--sk-sage-grey);border-radius:16px;padding:1.125rem;min-width:0}
.sk-fcov__map h3{font-family:var(--sk-sans);font-size:.82rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;margin:0 0 .75rem;color:var(--sk-ink)}
.sk-fcov__areas{list-style:none;margin:0;padding:0;display:grid;gap:10px;grid-template-columns:repeat(auto-fill,minmax(min(100%,150px),1fr))}
.sk-fcov__area{background:var(--sk-white);border-radius:12px;padding:12px 14px;display:grid;gap:4px;min-width:0}
.sk-fcov__area strong{font-size:1.15rem;font-weight:800;color:var(--sk-sage)}
.sk-fcov__area span{font-size:.85rem;color:var(--sk-muted);overflow-wrap:anywhere}
.sk-fcov__empty{margin:0;font-size:.9rem;color:var(--sk-muted)}
.sk-fcov__standing{margin:0 0 1.5rem;display:grid;gap:.5rem}
.sk-fcov__standing p{margin:0}
.sk-fcov__blockers{margin:0;padding-left:1.25rem;display:grid;gap:4px;font-size:.9rem}
.sk-fcov__tradelist{display:flex;flex-wrap:wrap;gap:8px;list-style:none;margin:0;padding:0}
.sk-fcov__tradelist li{border:1px solid var(--sk-sage);background:var(--sk-sage-grey);border-radius:999px;padding:6px 12px;font-size:.9rem;font-weight:700;color:var(--sk-ink)}
`;

export const JOB_BOARD_CSS = `
.sk-jobs__head{display:flex;justify-content:space-between;align-items:end;gap:1rem;flex-wrap:wrap;margin-bottom:1.25rem}
.sk-jobs__head .sk-lead{margin-top:.75rem;max-width:58ch}
.sk-jobs__tools{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:1rem}
.sk-jobs__tools .sk-trade__field{flex:1 1 180px}
.sk-jobs__where{margin:0 0 1rem;font-size:.9rem;color:var(--sk-muted);overflow-wrap:anywhere}
.sk-jobs__list{list-style:none;margin:0;padding:0;display:grid;gap:12px}
.sk-jobs__job{background:var(--sk-white);border:1px solid var(--sk-border);border-radius:16px;padding:1.125rem 1.25rem;display:grid;gap:10px 20px;min-width:0}
@media (min-width:640px){.sk-jobs__job{grid-template-columns:minmax(0,1fr) auto}}
.sk-jobs__job[data-closed]{background:var(--sk-warm)}
.sk-jobs__job h3{font-family:var(--sk-sans);font-size:1.02rem;font-weight:800;line-height:1.35;margin:0;color:var(--sk-ink);overflow-wrap:anywhere}
.sk-jobs__where-line{margin:4px 0 0;font-size:.85rem;color:var(--sk-faint)}
.sk-jobs__kitchen{margin:4px 0 0;font-size:.9rem;color:var(--sk-muted)}
.sk-jobs__side{display:grid;gap:2px;align-content:start}
@media (min-width:640px){.sk-jobs__side{text-align:right}}
.sk-jobs__side b{font-size:.95rem}
.sk-jobs__side small{color:var(--sk-faint);font-size:.8rem}
.sk-jobs__tags{grid-column:1/-1;display:flex;gap:6px;flex-wrap:wrap;list-style:none;margin:0;padding:0}
.sk-jobs__tags li{font-size:.75rem;font-weight:700;background:var(--sk-stone);border-radius:999px;padding:4px 10px;color:var(--sk-ink)}
.sk-jobs__tags li.sk-jobs__tag--example{background:var(--sk-brass-soft);color:var(--sk-brass-text)}
.sk-jobs__tags li.sk-jobs__tag--closed{background:var(--sk-stone-deep)}
.sk-jobs__budget{grid-column:1/-1;margin:0;font-size:.85rem;color:var(--sk-muted)}
.sk-jobs__cta{grid-column:1/-1;display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.sk-jobs__sent{display:inline-flex;align-items:center;gap:6px;font-weight:700;font-size:.9rem;color:var(--sk-sage)}
.sk-jobs__state{margin:0}
.sk-jobs__job[data-muted]{background:var(--sk-warm);color:var(--sk-muted)}
.sk-jobs__job[data-muted] h3{color:var(--sk-muted)}
.sk-jobs__pack{grid-column:1/-1;margin:0;font-size:.85rem;color:var(--sk-muted);white-space:pre-line}
.sk-jobs__won{grid-column:1/-1;margin:0;padding:.75rem 1rem;border-radius:12px;background:var(--sk-sage-grey);color:var(--sk-ink);font-size:.9rem;display:grid;gap:4px}
.sk-jobs__won strong{color:var(--sk-sage)}
.sk-jobs__mine{display:inline-flex;align-items:center;gap:6px;font-weight:700;font-size:.9rem;color:var(--sk-ink)}
.sk-jobs__dialog{width:min(560px,calc(100vw - 2rem));max-height:calc(100dvh - 2rem);border:0;border-radius:18px;padding:0;background:var(--sk-white);color:var(--sk-ink);box-shadow:var(--sk-shadow-lg)}
.sk-jobs__dialog::backdrop{background:rgba(29,33,31,.45)}
.sk-jobs__form{padding:clamp(1.125rem,.75rem + 1.5vw,1.75rem);display:grid;gap:1rem}
.sk-jobs__form h2{font-size:1.4rem}
.sk-jobs__form .sk-trade__field + .sk-trade__field{margin-top:0}
.sk-jobs__formgrid{display:grid;gap:1rem}
@media (min-width:520px){.sk-jobs__formgrid{grid-template-columns:1fr 1fr}}
.sk-jobs__money{position:relative}
.sk-jobs__money span{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:var(--sk-faint);pointer-events:none}
.sk-jobs__money input{padding-left:28px}
.sk-jobs__textarea{min-height:110px;resize:vertical}
.sk-jobs__count{font-size:.8rem;color:var(--sk-faint);margin:0;text-align:right}
.sk-jobs__formactions{display:flex;flex-wrap:wrap;gap:.75rem;justify-content:flex-end}
`;
